import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { api } from '@/lib/api';
import { useAuthStore, User } from '@/store/auth.store';
import { identifyUser, resetUser } from '@/lib/posthog';

const REDIRECT_STORAGE_KEY = 'auth_redirect_path';

interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => void;
  loginWithFacebook: () => void;
  refreshUser: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  captchaToken?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

function getRedirectPath(location: ReturnType<typeof useLocation>, searchParams: URLSearchParams): string {
  // 1. Check location.state.from (set by ProtectedRoute — may be a Location object or string)
  const stateFrom = (location.state as { from?: { pathname?: string } | string })?.from;
  const fromPath = typeof stateFrom === 'string' ? stateFrom : stateFrom?.pathname;
  if (fromPath && fromPath.startsWith('/')) return fromPath;

  // 2. Check ?returnTo= query param
  const returnTo = searchParams.get('returnTo');
  if (returnTo && returnTo.startsWith('/')) return returnTo;

  // 3. Fallback
  return '/app/dashboard';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, accessToken, isAuthenticated, setAuth, setUser, logout: clearAuth } = useAuthStore();

  useEffect(() => {
    const checkAuth = async () => {
      if (accessToken) {
        try {
          // api.get auto-refreshes on 401, so this will transparently
          // use the refresh token if the access token has expired
          const userData = await api.get<any>('/auth/me', { token: accessToken });
          setUser(userData);
          Sentry.setUser({ id: userData._id, email: userData.email });
          identifyUser(userData._id, { email: userData.email, name: `${userData.firstName} ${userData.lastName}` });
        } catch {
          clearAuth();
          Sentry.setUser(null);
          resetUser();
        }
      }
      setIsLoading(false);
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string, captchaToken?: string) => {
    const response = await api.post<any>('/auth/login', { email, password, captchaToken });
    setAuth({
      user: response.user,
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    });
    Sentry.setUser({ id: response.user._id, email: response.user.email });
    identifyUser(response.user._id, { email: response.user.email, name: `${response.user.firstName} ${response.user.lastName}` });
    navigate(getRedirectPath(location, searchParams));
  };

  const register = async (data: RegisterData) => {
    const response = await api.post<any>('/auth/register', data);
    setAuth({
      user: response.user,
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    });
    Sentry.setUser({ id: response.user._id, email: response.user.email });
    identifyUser(response.user._id, { email: response.user.email, name: `${response.user.firstName} ${response.user.lastName}` });
    navigate(getRedirectPath(location, searchParams));
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', {}, { token: accessToken ?? undefined });
    } catch {
      // Ignore errors
    }
    clearAuth();
    Sentry.setUser(null);
    resetUser();
    navigate('/app/login');
  };

  const loginWithGoogle = () => {
    const redirectPath = getRedirectPath(location, searchParams);
    if (redirectPath !== '/app/dashboard') {
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, redirectPath);
    }
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    window.location.href = `${apiUrl}/auth/google`;
  };

  const loginWithFacebook = () => {
    const redirectPath = getRedirectPath(location, searchParams);
    if (redirectPath !== '/app/dashboard') {
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, redirectPath);
    }
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    window.location.href = `${apiUrl}/auth/facebook`;
  };

  const refreshUser = async () => {
    if (accessToken) {
      try {
        const userData = await api.get<any>('/auth/me', { token: accessToken });
        setUser(userData);
      } catch {
        // Ignore errors
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated,
        user,
        login,
        register,
        logout,
        loginWithGoogle,
        loginWithFacebook,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
